/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {GenericObject} from '@ion/core/src/types/entities';
import DashboardList, {Dashboard, DashboardCategory} from './DashboardList';
import DashboardNavHeader from './DashboardNavHeader';

/**
 * Props for the DashboardDrawerSection component.
 */
export interface DashboardDrawerSectionProps {
  openCategory: Record<string, boolean>;
  toggleCategory: (category: string | string[], value?: boolean) => void;
  selectedDashboard: Dashboard;
  selectedCategory?: string;
  setSelectedDashboard: (dashboard: GenericObject) => void;
  onTabChange: (tabIndex: number) => void;
  resetSelection: () => void;
  categories: DashboardCategory[];
  isLoading: boolean;
  isAdmReports?: boolean;
  showScheduledReports?: boolean;
  classes: {
    navbar: string;
    desktopSideNav: string;
  };
};

/**
 * DashboardDrawerSection renders the left-hand section of the dashboard interface,
 * including a header for navigation and a list of dashboard categories.
 *
 * - Displays a nav header with tab controls and a back/reset option
 * - Shows the list of dashboard categories in a scrollable section
 * - Highlights the selected dashboard and allows changing it
 *
 * This component is used within a drawer or sidebar layout, especially
 * in desktop views of analytics dashboards.
 *
 * ```typescript
 * <DashboardDrawerSection openCategory="Sales"
 *   toggleCategory={(cat) => setOpenCategory(cat)}
 *   selectedDashboard={dashboard}
 *   setSelectedDashboard={(d) => setDashboard(d)}
 *   onTabChange={(index) => setActiveTab(index)}
 *   resetSelection={() => clearDashboardSelection()}
 *   categories={['Sales', 'Finance', 'Operations']}
 *   isLoading={false}
 *   classes={{
 *     navbar: 'dashboard-navbar',
 *     desktopSideNav: 'side-nav-panel'
 *   }} />
 * ```
 */
export const DashboardDrawerSection = ({
  openCategory,
  toggleCategory,
  selectedDashboard,
  selectedCategory,
  setSelectedDashboard,
  onTabChange,
  resetSelection,
  categories,
  isLoading,
  isAdmReports = false,
  showScheduledReports = false,
  classes
}: DashboardDrawerSectionProps) => (
  <div className={classes.navbar}>
    <DashboardNavHeader selected={selectedDashboard}
      onTabChange={onTabChange}
      isAdmReports={isAdmReports}
      onBack={resetSelection}/>
    <div className={classes.desktopSideNav}>
      <DashboardList categories={categories}
        openCategory={openCategory}
        selectedCategory={selectedCategory}
        toggleCategory={toggleCategory}
        isLoading={isLoading}
        showScheduledReports={showScheduledReports}
        selected={selectedDashboard}
        setSelected={setSelectedDashboard}/>
    </div>
  </div>
);

export default DashboardDrawerSection;
